import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { OpenInNew } from '@mui/icons-material'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Skeleton, Stack, Typography } from '@mui/material'

import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog'
import NewsCard from '../../../../../features/news/components/NewsCard/NewsCard'
import { NewsEntry } from '../../../../../features/news/types/UnifiedNewsEntry'
import { ConfirmDialogData } from '../../../../../types/ConfirmDialogData'
import {
  useCloneAnalysisToMarketMutation,
  useGetAnalysisNewsEntryQuery,
  useGetAnalyzedVersionsPerMarketQuery,
  useGetGameVersionsQuery,
} from '../../../../api/game-analyses'
import { AnalyzedVersionsPerMarket } from '../../../games/components/AnalyzedVersionsPerMarket/AnalyzedVersionsPerMarket'
import { getBadgeCloneSourceIso } from '../../../games/helpers/analysisListHelpers'
import { AnalyzedVersionPerMarket, latestGameVersion } from '../../../games/types/GameAnalyses'
import { InternalGameDocument } from '../../../games/types/InternalGame'
import { newsCardDelayMs } from '../../helpers/analysisOverviewHelpers'

type AnalysisResultDialogProps = {
  open: boolean
  analysisId: string
  gameId: string
  game: InternalGameDocument
  onClose: () => void
  onCloned: (analysisId: string, comparedMarketIso?: string) => void
  onCloneFailed: () => void
}

const usMarketIso = 'us'

export const AnalysisResultDialog: FC<AnalysisResultDialogProps> = ({ open, analysisId, gameId, game, onClose, onCloned, onCloneFailed }) => {
  const { t } = useTranslation()
  // Legacy waits for the calculation to write the news card.
  const [canLoadNewsCard, setCanLoadNewsCard] = useState(false)
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<AnalyzedVersionPerMarket>>()

  useEffect(() => {
    setCanLoadNewsCard(false)
    if (!open) return
    const timeout = setTimeout(() => setCanLoadNewsCard(true), newsCardDelayMs)
    return () => clearTimeout(timeout)
  }, [open, analysisId])

  const appId = game.appId || undefined
  const newsQuery = useGetAnalysisNewsEntryQuery(analysisId, { skip: !open || !canLoadNewsCard, refetchOnMountOrArgChange: true })
  const perMarketQuery = useGetAnalyzedVersionsPerMarketQuery(gameId, { skip: !open, refetchOnMountOrArgChange: true })
  const usVersionsQuery = useGetGameVersionsQuery({ appId: appId ?? 0, marketIso: usMarketIso }, { skip: !open || !appId })
  const [cloneAnalysisToMarket, cloneState] = useCloneAnalysisToMarketMutation()

  const perMarket = useMemo(() => perMarketQuery.data ?? [], [perMarketQuery.data])
  const usVersions = useMemo(() => usVersionsQuery.data ?? [], [usVersionsQuery.data])
  const badgeSourceIso = getBadgeCloneSourceIso(usVersions, perMarket)
  const entry = newsQuery.data
  const newsEntry = entry?.id && entry.entryData ? { feedEntry: entry as unknown as NewsEntry, hasFTUEVideo: 0 } : undefined

  const handleConfirm = async (confirmed?: ConfirmDialogData<AnalyzedVersionPerMarket>) => {
    setConfirmDialogData(undefined)
    const target = confirmed?.data
    if (!target || !badgeSourceIso) return
    try {
      const id = await cloneAnalysisToMarket({
        gameId,
        sourceMarketIso: badgeSourceIso,
        targetMarketIso: target.marketIso,
        gameVersion: latestGameVersion,
      }).unwrap()
      onCloned(id, badgeSourceIso !== target.marketIso ? target.marketIso : undefined)
    } catch {
      onCloneFailed()
    }
  }

  const renderNewsCard = () => {
    if (!canLoadNewsCard || newsQuery.isFetching) return <Skeleton variant="rectangular" height={160} />
    if (newsQuery.isError) return <Typography color="error.main">{t('internal-analysts:analysis_result_news_failed')}</Typography>
    if (!newsEntry) return <Typography color="text.secondary">{t('internal-analysts:analysis_result_news_none')}</Typography>
    return (
      <Stack spacing={1}>
        <Box sx={{ pointerEvents: 'none' }}>
          <NewsCard newsEntry={newsEntry} />
        </Box>
        <Box textAlign="center">
          <Button endIcon={<OpenInNew />} href={`/news-card/${newsEntry.feedEntry.id}`} target="_blank" rel="noopener noreferrer">
            {t('internal-analysts:analysis_result_open_news')}
          </Button>
        </Box>
      </Stack>
    )
  }

  return (
    <Dialog open={open} fullWidth maxWidth="md">
      <DialogTitle textAlign="center">{t('internal-analysts:analysis_result_title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          {renderNewsCard()}
          <AnalyzedVersionsPerMarket
            newestVersion={usVersions[0]?.version}
            perMarket={perMarket}
            isLoading={perMarketQuery.isFetching || usVersionsQuery.isFetching}
            canClone={!!badgeSourceIso && !cloneState.isLoading}
            onClone={(target) =>
              setConfirmDialogData({
                title: t('internal-analysts:games_analyses_clone_latest'),
                confirmText: t('internal-analysts:games_analyses_clone_latest_confirm', { market: target.marketName }),
                actionText: t('internal-analysts:games_analyses_clone'),
                data: target,
              })
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={!canLoadNewsCard || newsQuery.isFetching} onClick={() => newsQuery.refetch()}>
          {t('internal-analysts:analysis_result_reload_news')}
        </Button>
        <Button variant="contained" disabled={cloneState.isLoading} onClick={onClose}>
          {t('internal-analysts:analysis_result_ok')}
        </Button>
      </DialogActions>
      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirm} />
    </Dialog>
  )
}
